import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signIn } from "../../actions/authActions";
import useForm, { FormContext } from "react-hook-form";
import {
  Box,
  Typography,
  Container,
  Button,
  IconButton,
  Divider,
  makeStyles,
  useTheme,
  useMediaQuery,
  Paper
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { Visibility, VisibilityOff } from "@material-ui/icons";

import TextField from "../form/TextField";
import GoogleLogin from "../auth/GoogleLogin";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: "60rem",
    margin: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: theme.spacing(2, 1),
    },
  },
  heading: {
    marginBottom: theme.spacing(3),
    textAlign: "center",
    fontFamily: "Kosugi Maru, sans-serif",
  },
  toggleGroup: {
    width: "100%",
    marginBottom: theme.spacing(2),
    fontSize: "1.6rem",
  },
  toggleButton: {
    width: "50%",
    border: `1px solid ${theme.palette.primary.main}`,
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: "#ffffff",
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "1.2rem",
    marginTop: theme.spacing(0.5),
  },
  passwordField: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  visibilityIcon: {
    color: "#1a237e",
    padding: theme.spacing(1),
    marginTop: "10px",
    "& svg": {
      fontSize: "2.5rem",
    },
  },
  actionArea: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
  links: {
    "& > a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      display: "block",
      marginBottom: theme.spacing(1),
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  dividerSection: {
    margin: theme.spacing(3, 0),
    textAlign: "center",
    position: "relative",
    "& hr": {
      margin: theme.spacing(2, 0),
    },
  },
  formContainer: {
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      width: "100%",
      margin: 0,
      borderRadius: 0,
    },
  },
  submitButton: {
    fontSize: "1.6rem",
    padding: theme.spacing(1, 3),
  },
}));

function Signin(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const methods = useForm();
  const [showPassword, setShowPassword] = useState(false);
  const { register, setValue, errors, handleSubmit, watch } = methods;
  const userType = watch("user_type");

  useEffect(() => {
    register({ name: "user_type" }, { required: true });
  }, [register]);

  const handleUserTypeChange = (event, newValue) => {
    if (newValue !== null) {
      setValue("user_type", newValue);
    }
  };

  const onSubmit = data => {
    dispatch(signIn(data.email, data.password));
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Box className={classes.root}>
        <Typography variant="h4" component="h1" className={classes.heading}>
          ログイン
        </Typography>

        <Paper className={classes.formContainer} elevation={1}>
          <FormContext {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box mb={3}>
                <Typography variant="body1" gutterBottom>
                  アカウントタイプ
                </Typography>
                <ToggleButtonGroup
                  value={userType}
                  exclusive
                  onChange={handleUserTypeChange}
                  className={classes.toggleGroup}
                >
                  <ToggleButton value="Personal" className={classes.toggleButton}>
                    求職者
                  </ToggleButton>
                  <ToggleButton value="Business" className={classes.toggleButton}>
                    企業・雇用主
                  </ToggleButton>
                </ToggleButtonGroup>
                {errors["user_type"] && (
                  <Typography className={classes.errorText}>
                    アカウントタイプを選んでください
                  </Typography>
                )}
              </Box>
              <Box mb={3}>
                <TextField
                  label="Email"
                  type="email"
                  placeholder="Ｅメールアドレスを入力してください"
                  name="email"
                  registerOptions={{ required: true, maxLength: 100 }}
                  fullWidth
                />
              </Box>

              <Box className={classes.passwordField}>
                <Box flexGrow={1}>
                  <TextField
                    label="パスワード"
                    type={showPassword ? "text" : "password"}
                    placeholder="パスワードを入力してください"
                    name="password"
                    registerOptions={{
                      required: true,
                      minLength: 6,
                      maxLength: 50
                    }}
                    fullWidth
                  />
                </Box>
                <IconButton
                  onClick={togglePassword}
                  edge="end"
                  className={classes.visibilityIcon}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </Box>

              <Box className={classes.actionArea}>
                <Box className={classes.links}>
                  <Link to="/auth/forgotpassword">
                    パスワードを忘れた方
                  </Link>
                  <Link to="/signup">
                    初めてご利用の方（新規登録）
                  </Link>
                </Box>
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                className={classes.submitButton}
              >
                ログイン
              </Button>
            </form>
          </FormContext>

          <Box className={classes.dividerSection}>
            <Divider />
            <Typography variant="body1">もしくは</Typography>
          </Box>

          <GoogleLogin userType={userType} />
        </Paper>
      </Box>
    </Container>
  );
}

export default Signin;
